
import React from "react";
import { X, Clock, Plus, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { User, FriendWithStatus } from "@/types";
import { cn } from "@/lib/utils";
import AvatarPlaceholder from "@/components/ui/avatar-placeholder";
import { Badge } from "@/components/ui/badge";

interface GolferSelectorProps {
  selectedGolfers: User[];
  onGolferSelect: (golfer: User) => void;
  onGolferRemove: (golferId: string) => void;
  allGolfers: User[];
  className?: string;
  onAddFriend?: () => void;
  onCreateManualPlayer?: (name: string, email?: string) => Promise<{ user?: User; sentRequest?: boolean }>;
  // For backward compatibility
  golfers?: User[];
  onGolfersSelected?: (golfers: User[]) => void;
  onBack?: () => void;
  // Friend type props
  confirmedFriends?: FriendWithStatus[];
  pendingFriends?: FriendWithStatus[];
  showFriendStatus?: boolean;
}

const GolferSelector = ({
  selectedGolfers,
  onGolferSelect,
  onGolferRemove,
  allGolfers,
  className,
  onAddFriend,
  onCreateManualPlayer,
  // Backward compatibility props
  golfers,
  onGolfersSelected,
  onBack,
  // Friend type props
  confirmedFriends,
  pendingFriends,
  showFriendStatus = false,
}: GolferSelectorProps) => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [showManualForm, setShowManualForm] = React.useState(false);
  const [manualName, setManualName] = React.useState("");
  const [manualEmail, setManualEmail] = React.useState("");
  const [isCreatingManual, setIsCreatingManual] = React.useState(false);

  // Use the new friend props if provided, otherwise fall back to allGolfers
  const allAvailableGolfers = React.useMemo(() => {
    if (confirmedFriends || pendingFriends) {
      return [
        ...(confirmedFriends || []),
        ...(pendingFriends || []),
      ];
    }
    return allGolfers;
  }, [allGolfers, confirmedFriends, pendingFriends]);

  // Filter golfers based on search term AND remove any that are already selected
  const filteredGolfers = React.useMemo(() => {
    return allAvailableGolfers.filter(
      (golfer) => {
        const nameMatches = golfer.name.toLowerCase().includes(searchTerm.toLowerCase());
        const isAlreadySelected = selectedGolfers.some(
          (selected) => selected.id === golfer.id
        );
        return nameMatches && !isAlreadySelected;
      }
    );
  }, [allAvailableGolfers, selectedGolfers, searchTerm]);

  // Helper function to determine if a golfer is a pending friend
  const isPendingFriend = (golfer: User): boolean => {
    if (!pendingFriends) return false;
    return pendingFriends.some(friend => friend.id === golfer.id);
  };

  // Ensure we're not seeing duplicates in the listing by checking unique IDs
  const uniqueFilteredGolfers = React.useMemo(() => {
    const uniqueIds = new Set();
    return filteredGolfers.filter(golfer => {
      if (uniqueIds.has(golfer.id)) {
        return false;
      }
      uniqueIds.add(golfer.id);
      return true;
    });
  }, [filteredGolfers]);

  const handleCreateManualPlayer = async () => {
    if (!manualName.trim() || !onCreateManualPlayer) return;
    setIsCreatingManual(true);
    try {
      const result = await onCreateManualPlayer(
        manualName.trim(),
        manualEmail.trim() || undefined
      );
      if (result.user) {
        onGolferSelect(result.user);
      }
      // Reset form on success (whether created or sent request)
      setManualName("");
      setManualEmail("");
      setShowManualForm(false);
    } finally {
      setIsCreatingManual(false);
    }
  };

  return (
    <div className={cn("flex flex-col", className)}>
      {/* Selected golfers row */}
      <div className="flex flex-wrap gap-2 mb-4">
        {selectedGolfers.map((golfer) => (
          <div
            key={golfer.id}
            className={cn(
              "flex items-center gap-2 rounded-full px-3 py-1 text-sm",
              golfer.isSample ? "bg-violet-50 border border-violet-200" :
              golfer.isManual ? "bg-amber-50 border border-amber-200" : "bg-forest/10"
            )}
          >
            <AvatarPlaceholder
              user={golfer}
              size="sm"
            />
            <span>{golfer.name}</span>
            {golfer.isSample && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 bg-violet-100 text-violet-700 border-none">
                Sample
              </Badge>
            )}
            {golfer.isManual && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 bg-amber-100 text-amber-700 border-none">
                Manual
              </Badge>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 rounded-full hover:bg-forest hover:text-white p-0"
              onClick={() => onGolferRemove(golfer.id)}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>

      {/* Search input */}
      <div className="relative mb-2">
        <Input
          type="text"
          placeholder="Search players..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full"
        />
      </div>

      {/* Results list */}
      <div className="max-h-60 overflow-y-auto border rounded-md bg-white">
        {uniqueFilteredGolfers.length > 0 ? (
          uniqueFilteredGolfers.map((golfer) => (
            <React.Fragment key={golfer.id}>
              <div
                className="flex items-center justify-between p-2 hover:bg-forest/5 cursor-pointer"
                onClick={() => onGolferSelect(golfer)}
              >
                <div className="flex items-center gap-2">
                  <AvatarPlaceholder
                    user={golfer}
                    size="sm"
                  />
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span>{golfer.name}</span>
                      {golfer.isSample && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0 bg-violet-100 text-violet-700 border-none">
                          Sample
                        </Badge>
                      )}
                      {golfer.isManual && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0 bg-amber-100 text-amber-700 border-none">
                          Manual
                        </Badge>
                      )}
                    </div>
                    {showFriendStatus && isPendingFriend(golfer) && (
                      <div className="flex items-center gap-1 text-xs text-amber-600 mt-0.5">
                        <Clock className="h-3 w-3" />
                        <span>Request sent</span>
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 rounded-full hover:bg-forest hover:text-white p-0"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <Separator />
            </React.Fragment>
          ))
        ) : (
          <div className="p-4 text-center text-gray-500">
            {searchTerm ? (
              <p>No players found matching "{searchTerm}"</p>
            ) : (
              <p>No players available to add</p>
            )}
            {onAddFriend && (
              <Button
                variant="link"
                onClick={onAddFriend}
                className="mt-2 text-forest"
              >
                Add a friend
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Add player manually section */}
      {onCreateManualPlayer && (
        <div className="mt-3">
          {!showManualForm ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground w-full justify-start"
              onClick={() => setShowManualForm(true)}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add player manually
            </Button>
          ) : (
            <div className="border rounded-md p-3 space-y-2 bg-muted/30">
              <p className="text-xs text-muted-foreground font-medium">Add a player without an account</p>
              <Input
                type="text"
                placeholder="Name (required)"
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
                autoFocus
              />
              <Input
                type="email"
                placeholder="Email (optional)"
                value={manualEmail}
                onChange={(e) => setManualEmail(e.target.value)}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="bg-forest hover:bg-forest/90"
                  onClick={handleCreateManualPlayer}
                  disabled={!manualName.trim() || isCreatingManual}
                >
                  {isCreatingManual ? "Adding..." : "Add Player"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShowManualForm(false);
                    setManualName("");
                    setManualEmail("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GolferSelector;
